/**
 * Smolit's dock, reproduced from the export.
 *
 * 400px on the right over a dimmed page, a header tinted from `#EAF7F6` to
 * white with the mark at 40px and "Active · thinks across Aotearoa" under the
 * name, the thread, the example chips, and the composer with its moana send
 * button. Every colour, radius and type size is a token already in the design
 * system; nothing here was invented.
 *
 * Two things the export does not have, both recorded in
 * docs/design-fidelity.md: the confirmation card, because a prototype that
 * cannot book does not need to ask; and the focus and Escape behaviour, because
 * the prototype's dimmed page is still perfectly reachable with Tab.
 */

import { useEffect, useRef, useState } from 'react';
import { Button, CloseIcon, PromptChip, SendIcon, SmolitMark, cn } from '@openroad/ui';
import { EXAMPLE_PROMPTS, useSmolit, type ThreadItem } from '../../lib/useSmolit';
import { useIsMobileLayout } from '../../lib/viewport';
import { SmolitResult } from './SmolitResult';

/**
 * A proposed action, and the only way one ever happens.
 *
 * The card carries the whole of what would be done — the message in full, the
 * total in full — because approving something you cannot read is not approving
 * anything.
 */
function ConfirmationCardView({
  item,
  onDecide,
  disabled,
}: {
  item: Extract<ThreadItem, { kind: 'card' }>;
  onDecide: (decision: 'approve' | 'decline') => void;
  disabled: boolean;
}) {
  const caution = item.card.tone === 'caution';
  // `settling` keeps the buttons — disabled — because nothing has been decided
  // yet and a card that vanishes mid-request looks like it worked.
  const settled = item.state !== 'open' && item.state !== 'settling';
  const sending = item.state === 'settling';

  return (
    <div
      className={cn(
        'ml-9 rounded-lg border bg-surface p-3.5',
        caution
          ? 'border-[color:var(--color-pohutukawa)]/25 bg-pohutukawa-tint'
          : 'border-moana-line bg-moana-tint',
      )}
    >
      <div className="text-label font-bold text-ink">{item.card.title}</div>
      <p className="m-0 mt-1 text-mini leading-normal text-ink-2">{item.card.summary}</p>

      {item.card.details.length > 0 ? (
        <dl className="m-0 mt-2.5 flex flex-col gap-1">
          {item.card.details.map((detail) => (
            <div key={detail.label} className="flex gap-2 text-mini">
              <dt className="min-w-[74px] flex-none text-subtle">{detail.label}</dt>
              <dd className="m-0 flex-1 text-ink">{detail.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {settled ? (
        <p className="m-0 mt-2.5 text-mini font-semibold text-muted">
          {item.state === 'approved'
            ? 'Approved.'
            : item.state === 'declined'
              ? 'Declined — nothing happened.'
              : 'That one is no longer open.'}
        </p>
      ) : (
        <div className="mt-3 flex items-center gap-2">
          <Button
            variant={caution ? 'cta' : 'primary'}
            size="sm"
            className="flex-1"
            disabled={disabled || sending}
            onClick={() => onDecide('approve')}
          >
            {item.card.confirmLabel}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled || sending}
            onClick={() => onDecide('decline')}
          >
            No thanks
          </Button>
          {sending ? <span className="text-mini text-subtle">Sending…</span> : null}
        </div>
      )}
    </div>
  );
}

function Bubble({ item }: { item: Extract<ThreadItem, { kind: 'member' | 'smolit' }> }) {
  const mine = item.kind === 'member';

  return (
    <div className={cn('flex items-start', mine ? 'justify-end' : 'justify-start')}>
      {!mine ? (
        <span className="mr-2 flex-none">
          <SmolitMark size={28} />
        </span>
      ) : null}
      <div className={mine ? 'max-w-[82%]' : 'max-w-[88%]'}>
        {item.text ? (
          <div
            className={cn(
              'px-3.5 py-[11px] text-body leading-normal whitespace-pre-wrap',
              mine
                ? 'rounded-[14px_4px_14px_14px] bg-ngahere text-white'
                : 'rounded-[4px_14px_14px_14px] bg-hover text-ink',
            )}
          >
            {item.text}
          </div>
        ) : null}
        {item.kind === 'smolit' && item.attachments.length > 0 ? (
          <div className={cn('flex flex-col gap-2', item.text && 'mt-2.5')}>
            {item.attachments.map((attachment, index) => (
              <SmolitResult key={index} attachment={attachment} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Three dots while a tool runs, named so the member knows what is happening. */
function Working({ activity }: { activity: string | null }) {
  return (
    <div className="flex items-center gap-2 pl-9 text-mini text-muted">
      <span className="flex gap-[3px]" aria-hidden>
        {[0, 1, 2].map((dot) => (
          <span
            key={dot}
            className="h-[5px] w-[5px] animate-pulse rounded-full bg-moana"
            style={{ animationDelay: `${dot * 160}ms` }}
          />
        ))}
      </span>
      {activity ?? 'Thinking'}…
    </div>
  );
}

export function SmolitPanel() {
  const { open, closePanel, thread, busy, activity, config, send, decide } = useSmolit();
  const mobile = useIsMobileLayout();
  const [draft, setDraft] = useState('');

  const panel = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  // Focus moves in on open and back to whatever opened it on close. Without
  // this the dimmed page behind stays in the tab order, invisible and
  // perfectly reachable — the same failure the sheet fixed in phase 1.
  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement;
    input.current?.focus();
    return () => {
      if (opener.current instanceof HTMLElement) opener.current.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closePanel();
        return;
      }
      if (event.key !== 'Tab' || !panel.current) return;

      const focusable = panel.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, closePanel]);

  // Follow the answer as it types.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [thread, busy]);

  if (!open) return null;

  const submit = () => {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft('');
    send(text);
  };

  return (
    <div className={cn('fixed inset-0 z-[60] flex', mobile ? 'items-end' : 'justify-end')}>
      <button
        type="button"
        aria-label="Close Smolit"
        tabIndex={-1}
        onClick={closePanel}
        className="absolute inset-0 cursor-default border-none bg-[rgb(26_31_28_/_0.32)] p-0"
      />

      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Smolit"
        className={cn(
          'relative flex flex-col bg-surface',
          // A 400px dock hanging off the right edge is a desktop shape. On a
          // phone it is the same panel as a bottom sheet, at the same height and
          // with the same corner radius the design system's own `Sheet` uses in
          // that placement (ADR-0010) — no new value, a different edge.
          mobile
            ? 'max-h-[88vh] w-full rounded-t-hero border-t border-line pb-safe shadow-sheet motion-safe:animate-sheet-up'
            : 'h-full w-[400px] max-w-[92vw] border-l border-line shadow-dock motion-safe:animate-sheet',
        )}
      >
        {mobile ? (
          <div className="flex justify-center pt-2.5 pb-1">
            <span className="h-1 w-9 rounded-full bg-line-firm" />
          </div>
        ) : null}

        <header className="flex items-center gap-[11px] border-b border-line-soft bg-linear-to-b from-moana-tint to-surface px-[18px] py-4">
          <SmolitMark size={40} />
          <div className="flex-1">
            <div className="font-display text-lead font-bold text-moana-deep">Smolit</div>
            <div className="flex items-center gap-[5px] text-mini text-moana-ink">
              <span className="h-[6px] w-[6px] rounded-full bg-success" />
              Active · thinks across Aotearoa
            </div>
          </div>
          <button
            type="button"
            onClick={closePanel}
            aria-label="Close Smolit"
            className={cn(
              'flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border',
              'border-line bg-surface text-muted transition-colors duration-fast',
              'ease-standard hover:bg-hover',
            )}
          >
            <CloseIcon size={16} />
          </button>
        </header>

        <div className="scrollbar-none flex flex-1 flex-col gap-3.5 overflow-y-auto p-[18px]">
          {thread.map((item) =>
            item.kind === 'card' ? (
              <ConfirmationCardView
                key={item.id}
                item={item}
                disabled={busy}
                onDecide={(decision) => decide(item.approvalId, decision)}
              />
            ) : (
              <Bubble key={item.id} item={item} />
            ),
          )}
          {busy ? <Working activity={activity} /> : null}
          <div ref={bottom} />
        </div>

        <div className="border-t border-line-soft px-3.5 py-3">
          <div className="scrollbar-none mb-2.5 flex gap-[7px] overflow-x-auto">
            {EXAMPLE_PROMPTS.map((prompt) => (
              <PromptChip key={prompt} onClick={() => send(prompt)}>
                {prompt}
              </PromptChip>
            ))}
          </div>

          <div className="flex items-center gap-2 rounded-2xl border border-line bg-sunk py-1.5 pr-1.5 pl-3.5">
            <input
              ref={input}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submit();
              }}
              placeholder="Ask Smolit anything…"
              aria-label="Ask Smolit anything"
              // 14px, not the 15px the rest of the app's inputs use — the
              // export's Smolit composer is its own size.
              className="flex-1 border-none bg-transparent text-body outline-none placeholder:text-placeholder"
            />
            <button
              type="button"
              onClick={submit}
              disabled={busy || draft.trim() === ''}
              aria-label="Send"
              className={cn(
                'flex h-9 w-9 flex-none cursor-pointer items-center justify-center rounded-md',
                'border-none bg-moana text-white transition-colors duration-fast ease-standard',
                'hover:bg-moana-deep disabled:cursor-not-allowed disabled:opacity-45',
              )}
            >
              <SendIcon size={17} />
            </button>
          </div>

          {config?.offline ? (
            <p className="m-0 mt-2 text-micro text-subtle">
              Running offline on the built-in model — {config.toolCount} tools, of which{' '}
              {config.confirmingTools.length} always ask first.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
